import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { stockTransferPath } from '../../lib/slug';
import { catalogApi } from '../catalog/catalogApi';
import { inventoryApi } from '../inventory/inventoryApi';
import { transfersApi } from './transfersApi';
import '../requisitions/requisitions.css';
import './transfers.css';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function TransferFormPage() {
  const navigate = useNavigate();
  const t = useT();
  const toast = useToast();
  const { activePropertyId, hasPermission } = useProperty();
  const canTransfer = hasPermission('inventory.transfer');

  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [fromLocationId, setFromLocationId] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [transferDate, setTransferDate] = useState(todayIso());
  const [notes, setNotes] = useState('');

  const [sourceStock, setSourceStock] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [qty, setQty] = useState({}); // itemId -> string

  useEffect(() => {
    let active = true;
    Promise.all([
      catalogApi.listLocations(activePropertyId).catch(() => []),
      catalogApi.listUnits().catch(() => []),
    ]).then(([locs, us]) => {
      if (!active) return;
      setLocations(locs || []);
      setUnits(us || []);
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [activePropertyId]);

  // Load the source location's stock whenever it changes.
  useEffect(() => {
    if (!fromLocationId) { setSourceStock([]); setQty({}); return; }
    let active = true;
    setStockLoading(true);
    inventoryApi.stock({ locationIds: fromLocationId, includeZero: false, pageSize: 500, sortBy: 'item' })
      .then((res) => { if (active) setSourceStock(res?.items || []); })
      .catch(() => { if (active) setSourceStock([]); })
      .finally(() => { if (active) setStockLoading(false); });
    setQty({});
    return () => { active = false; };
  }, [fromLocationId]);

  const locationOptions = useMemo(
    () => [{ value: '', label: t('Select a location…') }, ...locations.map((l) => ({ value: l.id, label: l.name }))],
    [locations, t],
  );
  const toOptions = useMemo(
    () => [{ value: '', label: t('Select a location…') }, ...locations.filter((l) => l.id !== fromLocationId).map((l) => ({ value: l.id, label: l.name }))],
    [locations, fromLocationId, t],
  );

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;

  const filteredStock = useMemo(() => {
    const term = itemSearch.trim().toLowerCase();
    if (!term) return sourceStock;
    return sourceStock.filter((s) =>
      (s.itemName || '').toLowerCase().includes(term) || (s.sku || '').toLowerCase().includes(term));
  }, [sourceStock, itemSearch]);

  const setItemQty = (itemId, value) => setQty((prev) => ({ ...prev, [itemId]: value }));

  const selectedLines = useMemo(() =>
    sourceStock
      .map((s) => ({ stock: s, q: Number(qty[s.itemId]) }))
      .filter((x) => x.q > 0 && !Number.isNaN(x.q)),
    [sourceStock, qty],
  );

  const totalValue = useMemo(() => {
    const cur = sourceStock.find((s) => s.averageCost)?.averageCost?.currency || 'USD';
    const sum = selectedLines.reduce((a, x) => a + x.q * Number(x.stock.averageCost?.amount || 0), 0);
    return `${cur} ${formatAmount(sum)}`;
  }, [selectedLines, sourceStock]);

  const submit = useCallback(async (post) => {
    if (!fromLocationId || !toLocationId) { toast.error(t('Choose both a source and destination location')); return; }
    const overdrawn = selectedLines.find((x) => x.q > x.stock.quantityOnHand);
    if (overdrawn) { toast.error(t('Cannot transfer more {item} than the {quantity} on hand', { item: overdrawn.stock.itemName, quantity: overdrawn.stock.quantityOnHand })); return; }
    if (selectedLines.length === 0) { toast.error(t('Enter a quantity for at least one item')); return; }
    setBusy(true);
    try {
      const transfer = await transfersApi.create({
        fromLocationId,
        toLocationId,
        transferDate: transferDate ? new Date(transferDate).toISOString() : null,
        notes: notes.trim() || null,
        lines: selectedLines.map((x) => ({ itemId: x.stock.itemId, quantity: x.q, notes: null })),
        post,
      });
      toast.success(post ? t('Transfer {number} posted', { number: transfer.number }) : t('Draft transfer saved'));
      navigate(stockTransferPath(transfer));
    } catch (err) {
      toast.error(t('Could not create transfer'), { description: err?.message });
      setBusy(false);
    }
  }, [fromLocationId, toLocationId, transferDate, notes, selectedLines, navigate, toast, t]);

  if (!canTransfer) {
    return (
      <PageContainer size="md">
        <EmptyState icon={<Icon.Lock size={28} />} title={t('Not allowed')} description={t("You don't have permission to transfer stock.")} />
      </PageContainer>
    );
  }

  return (
    <PageContainer size="lg">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/transfers')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Transfers')}
      </Button>

      <PageHeader
        title={t('New transfer')}
        description={t('Move stock from one location to another. Pick the source and destination, then choose how much of each item to send.')}
        icon={<Icon.ArrowLeftRight size={22} />}
      />

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : locations.length < 2 ? (
        <Banner tone="warning">{t('At least two stock locations are needed to transfer between them. Add locations in Settings first.')}</Banner>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Card>
            <Card.Body>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                <div className="ps-field">
                  <span className="ps-field__label">{t('From location')}</span>
                  <Select value={fromLocationId} onChange={setFromLocationId} ariaLabel={t('From location')} options={locationOptions} searchable />
                </div>
                <div className="ps-field">
                  <span className="ps-field__label">{t('To location')}</span>
                  <Select value={toLocationId} onChange={setToLocationId} ariaLabel={t('To location')} options={toOptions} disabled={!fromLocationId} searchable />
                </div>
                <Input label={t('Transfer date')} type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
              </div>
            </Card.Body>
          </Card>

          {!fromLocationId ? (
            <Card><Card.Body><EmptyState icon={<Icon.ArrowLeftRight size={26} />} title={t('Choose a source location')} description={t("Pick where the stock is moving from to see what's available.")} /></Card.Body></Card>
          ) : (
            <Card>
              <Card.Header>
                <Card.Title>{t('Items to move')}</Card.Title>
                <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                  {t("Enter a quantity for each item you want to send. You can't transfer more than what's on hand at the source.")}
                </div>
              </Card.Header>
              <Card.Body>
                {stockLoading ? (
                  <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-10)' }}><Spinner size={22} /></div>
                ) : sourceStock.length === 0 ? (
                  <EmptyState icon={<Icon.Boxes size={26} />} title={t('No stock at this location')} description={t('The source location has no on-hand stock to transfer.')} />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <Input value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} placeholder={t('Search items…')} leadingIcon={<Icon.Search size={15} />} />
                    <div className="trf-picker">
                      {filteredStock.map((s) => {
                        const q = qty[s.itemId] ?? '';
                        const selected = Number(q) > 0;
                        const uom = uomCode(s.uomId) ? ` ${uomCode(s.uomId)}` : '';
                        return (
                          <div className={`trf-row ${selected ? 'is-selected' : ''}`} key={s.id}>
                            <div className="trf-row__item">
                              <div className="trf-row__name">{s.itemName || s.itemId}</div>
                              {s.sku && <div className="trf-row__sub">{s.sku}</div>}
                            </div>
                            <div className="trf-row__onhand">{t('{quantity} on hand', { quantity: `${s.quantityOnHand}${uom}` })}</div>
                            <div className="trf-row__qty">
                              <Input
                                type="number"
                                min="0"
                                max={s.quantityOnHand}
                                step="any"
                                value={q}
                                placeholder="0"
                                onChange={(e) => setItemQty(s.itemId, e.target.value)}
                              />
                            </div>
                          </div>
                        );
                      })}
                      {filteredStock.length === 0 && (
                        <div style={{ padding: 'var(--space-4)' }} className="pp-cell-muted">{t('No items match “{search}”.', { search: itemSearch })}</div>
                      )}
                    </div>

                    <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Optional — reason for the transfer')} />

                    <div className="trf-summary">
                      <div className="trf-route">
                        <Badge tone="neutral">{locations.find((l) => l.id === fromLocationId)?.name}</Badge>
                        <Icon.ArrowRight size={16} />
                        <Badge tone={toLocationId ? 'brand' : 'neutral'}>{locations.find((l) => l.id === toLocationId)?.name || t('destination')}</Badge>
                      </div>
                      <div>
                        <span className="pp-cell-muted">{selectedLines.length === 1 ? t('{count} item', { count: selectedLines.length }) : t('{count} items', { count: selectedLines.length })} · </span>
                        <strong>{totalValue}</strong>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                      <Button variant="primary" loading={busy} disabled={selectedLines.length === 0 || !toLocationId} leadingIcon={<Icon.Check size={16} />} onClick={() => submit(true)}>
                        {t('Post transfer')}
                      </Button>
                      <Button variant="secondary" loading={busy} disabled={selectedLines.length === 0 || !toLocationId} onClick={() => submit(false)}>
                        {t('Save as draft')}
                      </Button>
                      <Button variant="ghost" onClick={() => navigate('/transfers')} disabled={busy}>{t('Cancel')}</Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      )}
    </PageContainer>
  );
}
